import {
  formatRouletteProbabilityPercentBr,
  formatRouletteProbabilityTotalBr,
  probabilitiesSumValid,
  remainderRouletteProbabilityPercentForSlots,
  scaleRouletteProbabilityPercent,
  sumOthersRouletteProbabilitiesScaled,
  sumRouletteProbabilitiesPercent,
  sumRouletteProbabilitiesScaled,
} from '@/lib/rouletteProbability'

const TARGET_PERCENT = 100
const TARGET_SUM_SCALED = scaleRouletteProbabilityPercent(TARGET_PERCENT)

export type RouletteProbabilityBudgetStatus = 'empty' | 'under' | 'exact' | 'over'

export interface RouletteProbabilityBudget {
  totalPercent: number
  totalDisplay: string
  remainderPercent: number
  remainderDisplay: string
  remainderLabel: string
  status: RouletteProbabilityBudgetStatus
  isValid: boolean
  progressPercent: number
}

export interface RouletteSlotProbabilityHint {
  totalDisplay: string
  othersSumDisplay: string
  availableForPositionDisplay: string
  availableForPositionPercent: number
  currentSlotDisplay: string
}

export function getRouletteProbabilityBudget(
  slots: ReadonlyArray<{ probability: number }>
): RouletteProbabilityBudget {
  const totalScaled = sumRouletteProbabilitiesScaled(slots)
  const totalPercent = totalScaled / 1_000_000
  const totalDisplay = formatRouletteProbabilityTotalBr(totalPercent)
  const isValid = probabilitiesSumValid(slots)

  let status: RouletteProbabilityBudgetStatus = 'empty'
  if (slots.length > 0 && totalScaled === 0) {
    status = 'empty'
  } else if (isValid) {
    status = 'exact'
  } else if (totalScaled > TARGET_SUM_SCALED) {
    status = 'over'
  } else {
    status = 'under'
  }

  const remainderScaled = TARGET_SUM_SCALED - totalScaled
  const remainderPercent = remainderScaled / 1_000_000
  const remainderAbsDisplay = formatRouletteProbabilityTotalBr(Math.abs(remainderPercent))

  let remainderLabel: string
  let remainderDisplay: string

  if (status === 'exact') {
    remainderLabel = 'Distribuição completa'
    remainderDisplay = '0'
  } else if (status === 'over') {
    remainderLabel = 'Excedente (acima de 100%)'
    remainderDisplay = remainderAbsDisplay
  } else {
    remainderLabel = 'Ainda disponível'
    remainderDisplay = remainderAbsDisplay
  }

  const progressPercent = Math.min(100, Math.max(0, (totalScaled / TARGET_SUM_SCALED) * 100))

  return {
    totalPercent,
    totalDisplay,
    remainderPercent,
    remainderDisplay,
    remainderLabel,
    status,
    isValid,
    progressPercent,
  }
}

export function getRouletteSlotProbabilityHint(
  slots: ReadonlyArray<{ probability: number }>,
  slotIndex: number
): RouletteSlotProbabilityHint {
  const totalPercent = sumRouletteProbabilitiesPercent(slots)
  const current = slots[slotIndex]?.probability ?? 0
  const availableForPositionPercent = remainderRouletteProbabilityPercentForSlots(slots, slotIndex)

  const othersSumPercent = sumOthersRouletteProbabilitiesScaled(slots, slotIndex) / 1_000_000

  return {
    totalDisplay: formatRouletteProbabilityTotalBr(totalPercent),
    othersSumDisplay: formatRouletteProbabilityPercentBr(othersSumPercent),
    availableForPositionDisplay: formatRouletteProbabilityPercentBr(availableForPositionPercent),
    availableForPositionPercent,
    currentSlotDisplay: formatRouletteProbabilityPercentBr(current),
  }
}
